import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { Input, Tree } from 'antd';
import type RcTree from 'rc-tree';
import type { DataNode } from 'antd/es/tree';
import styles from './index.module.less';
import { useDebounceFn, useUnmount } from 'ahooks';
import type { ManualTreeRecord } from '@/api/sip/bookmap/manualTree';
import { useAutoHeight } from 'xui-pro';
import type { Timeout } from 'ahooks/lib/useRequest/src/types';
import { useTranslation } from '@/hooks/useTranslation';

export type SelectData = {
  uniqueCode: string;
  code: string;
  version: string;
  name: string;
  bookMapCode?: string;
};

export type CatalogTreeAction = {
  select: (sks: string[]) => void;
};

export type CatalogProps = {
  onSelect: (data: SelectData) => void;
  data: ManualTreeRecord[];
  select: SelectData | undefined;
  defaultSelected?: SelectData;
  bookMapCode: string;
};

const { DirectoryTree } = Tree;
const { Search } = Input;

function isTopic(type: string) {
  return type.toUpperCase() === 'TOPIC';
}

function travelNode(
  data: ManualTreeRecord[],
  parentKeys: string[],
  callback: (parentKeys: string[], node: ManualTreeRecord) => boolean
) {
  let isBreak = false;
  for (const node of data) {
    isBreak = callback([...parentKeys], node);
    if (isBreak) return true;
    if (node.childList && node.childList.length) {
      if (travelNode(node.childList, [...parentKeys, node.uniqueCode], callback)) {
        isBreak = true;
        return true;
      }
    }
  }
  return isBreak;
}

function getSelectData(data: ManualTreeRecord[], selectdKeys: string[]) {
  let result: { parentKeys: string[]; selectNode: ManualTreeRecord | undefined } = {
    parentKeys: [],
    selectNode: undefined
  };
  travelNode(data, [], (parentKeys, node) => {
    if (selectdKeys.length > 0) {
      if (node.uniqueCode === selectdKeys[0]) {
        result = { parentKeys, selectNode: node };
        return true;
      }
    } else {
      if (isTopic(node.type)) {
        result = { parentKeys, selectNode: node };
        return true;
      }
    }
    return false;
  });
  return result;
}

function getTreeData(data: ManualTreeRecord[], searchKey: string) {
  const tree: DataNode[] = data.map(group => {
    const strTitle = group.name as string;
    const index = strTitle.toLocaleLowerCase().indexOf(searchKey.toLocaleLowerCase());
    const beforeStr = strTitle.substring(0, index);
    const matchStr = strTitle.substring(index, index + searchKey.length);
    const afterStr = strTitle.substring(index + searchKey.length);
    const title =
      index > -1 ? (
        <span>
          {beforeStr}
          <span style={{ color: '#f50' }}>{matchStr}</span>
          {afterStr}
        </span>
      ) : (
        strTitle
      );
    return {
      title: title,
      key: group.uniqueCode,
      selectable: isTopic(group.type),
      children:
        group.childList && group.childList.length
          ? getTreeData(group.childList, searchKey)
          : undefined
    };
  });
  return tree;
}

const CatalogTree = forwardRef<CatalogTreeAction, CatalogProps>((props, ref) => {
  const $ = useTranslation();
  const [searchKey, setSearchKey] = useState<string>('');
  const treeData = useMemo(() => getTreeData(props.data, searchKey), [props.data, searchKey]);
  // 初始化树形数据、默认展开的节点、默认选中的节点
  const [{ defaultExpandedKeys, defaultSelectedKeys, defaultSelectNodes }] = useState(() => {
    const expandedKeys: string[] = [];
    const selectedKeys: string[] = [];
    const selectedNodes: ManualTreeRecord[] = [];
    const selectData = getSelectData(
      props.data,
      props.defaultSelected?.uniqueCode ? [props.defaultSelected.uniqueCode] : []
    );
    if (selectData && selectData.parentKeys) {
      expandedKeys.push(...selectData.parentKeys);
    }
    if (selectData && selectData.selectNode) {
      selectedKeys.push(selectData.selectNode.uniqueCode);
      selectedNodes.push(selectData.selectNode);
    }
    return {
      defaultExpandedKeys: expandedKeys,
      defaultSelectedKeys: selectedKeys,
      defaultSelectNodes: selectedNodes
    };
  });
  // 控制展开的节点
  const [expandedKeys, setExpandedKeys] = useState<any[]>(defaultExpandedKeys);
  // 控制选中的节点
  const oldDefaultSelectedKeys = useRef<string[]>();
  useEffect(() => {
    if (oldDefaultSelectedKeys.current === defaultSelectedKeys) return;
    oldDefaultSelectedKeys.current = defaultSelectedKeys;

    if (defaultSelectNodes.length > 0) {
      props.onSelect(defaultSelectNodes[0]);
    }
  }, [defaultSelectedKeys, defaultSelectNodes, props]);
  const selectedKeys = useMemo(() => {
    if (!props.select) return [];
    return [props.select.uniqueCode];
  }, [props.select]);
  const onSelect = useCallback(
    sks => {
      if (sks.length < 1) return;
      const selectData = getSelectData(props.data, sks);
      if (selectData?.selectNode) {
        props.onSelect({ ...selectData.selectNode });
      }
    },
    [props]
  );
  useImperativeHandle(
    ref,
    () => {
      return {
        select: (sks: string[]) => {
          if (sks.length < 1) return;
          const selectData = getSelectData(props.data, sks);
          if (selectData?.selectNode) {
            props.onSelect({ ...selectData.selectNode });
          }
          if (selectData.parentKeys.length > 0) {
            const newExpandedKeys = new Set(
              Array.from(expandedKeys).concat(Array.from(selectData.parentKeys))
            );
            setExpandedKeys(Array.from(newExpandedKeys));
          }
        }
      };
    },
    [expandedKeys, props]
  );

  const timeoutRef = useRef<Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const treeRef = useRef<RcTree<DataNode>>(null);
  const { _height: treeHeight } = useAutoHeight(containerRef, true, 0, 0, false, 24);

  const { run: onChange } = useDebounceFn(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      let newExpandedKeys: Set<string> = new Set();
      let foundNodeKey = '';

      if (value) {
        travelNode(props.data, [], (parentKeys, node) => {
          if ((node.name as string).toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) > -1) {
            newExpandedKeys = new Set(Array.from(newExpandedKeys).concat(Array.from(parentKeys)));
            if (!foundNodeKey) {
              foundNodeKey = node.uniqueCode;
            }
          }

          return false;
        });
        // 有搜索内容，则展开并跳转到命中的节点；否则不处理
        newExpandedKeys = new Set(Array.from(newExpandedKeys));
        setExpandedKeys(Array.from(newExpandedKeys));

        timeoutRef.current = setTimeout(() => {
          if (foundNodeKey && treeRef.current) {
            treeRef.current?.scrollTo({ key: foundNodeKey });
          }
        });
      }

      setSearchKey(value);
    },
    {
      wait: 500
    }
  );

  useUnmount(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  });

  return (
    <>
      <Search
        style={{ marginBottom: '8px', paddingRight: '16px' }}
        placeholder={$('搜索目录')}
        onChange={onChange}
      />
      <div ref={containerRef}>
        <DirectoryTree
          ref={treeRef}
          treeData={treeData}
          onSelect={onSelect}
          defaultSelectedKeys={defaultSelectedKeys}
          blockNode={true}
          className={styles['catalog-tree']}
          defaultCheckedKeys={defaultSelectedKeys}
          selectedKeys={selectedKeys}
          onExpand={setExpandedKeys}
          expandedKeys={expandedKeys}
          height={treeHeight as number}
        />
      </div>
    </>
  );
});

export default CatalogTree;
